/**
 * Main Zustand store combining all slices with devtools support
 */

import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

// import { createSelectors } from './helpers' // Unused for now
import type { AppStore } from './types';
import { create } from 'zustand';
import { createAuthSlice } from './slices/auth.slice';
import { createUISlice } from './slices/ui.slice';
import { createWorkspaceSlice } from './slices/workspace.slice';

/**
 * Main application store combining all slices
 * Uses devtools in development for debugging
 * Persists auth state to localStorage
 */
export const useStore = create<AppStore>()(
  devtools(
    persist(
      subscribeWithSelector((...args) => ({
        ...createAuthSlice(...args),
        ...createWorkspaceSlice(...args),
        ...createUISlice(...args),
      })),
      {
        name: 'sendguard-preferences',
        partialize: (state) => ({
          // Only persist UI preferences - NO TOKENS!
          theme: state.theme,
          sidebarState: state.sidebarState,
        }),
        onRehydrateStorage: () => (state) => {
          // Apply theme to DOM after rehydration
          if (state?.theme) {
            const root = document.documentElement;
            if (state.theme === 'system') {
              const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
              root.classList.toggle('dark', prefersDark);
            } else {
              root.classList.toggle('dark', state.theme === 'dark');
            }
          }
        },
      }
    ),
    {
      name: 'sendguard-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ============= Auto-generate individual selectors =============
// const selectors = createSelectors(useStore) // Unused for now

// ============= Typed Selector Hooks =============

/**
 * Auth selectors
 */
// Avoid returning a new object from a single selector, which
// can cause useSyncExternalStore to warn about uncached snapshots
// and create render loops in React 18. Instead, select each value
// independently and compose the return value.
export const useAuth = () => {
  const user = useStore((state) => state.user);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const isLoading = useStore((state) => state.isLoading);
  const login = useStore((state) => state.login);
  const logout = useStore((state) => state.logout);
  const updateUser = useStore((state) => state.updateUser);

  return { user, isAuthenticated, isLoading, login, logout, updateUser };
};

// Individual selectors for performance-critical components
export const useAuthUser = () => useStore((state) => state.user);
export const useIsAuthenticated = () => useStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useStore((state) => state.isLoading);

/**
 * Workspace selectors
 */
export const useWorkspace = () => {
  const currentWorkspace = useStore((state) => state.currentWorkspace);
  const currentOrganization = useStore((state) => state.currentOrganization);
  const workspaces = useStore((state) => state.workspaces);
  const isLoading = useStore((state) => state.isLoadingWorkspaces);
  const switchWorkspace = useStore((state) => state.switchWorkspace);
  const fetchWorkspaces = useStore((state) => state.fetchWorkspaces);

  return {
    currentWorkspace,
    currentOrganization,
    workspaces,
    isLoading,
    switchWorkspace,
    fetchWorkspaces,
  };
};

// Individual selectors for performance-critical components
export const useCurrentWorkspace = () => useStore((state) => state.currentWorkspace);
export const useCurrentOrganization = () => useStore((state) => state.currentOrganization);
export const useWorkspaces = () => useStore((state) => state.workspaces);
export const useWorkspaceMembers = () => useStore((state) => state.members);

/**
 * UI selectors
 */
export const useUI = () => {
  const theme = useStore((state) => state.theme);
  const sidebarState = useStore((state) => state.sidebarState);
  const isMobileMenuOpen = useStore((state) => state.isMobileMenuOpen);
  const setTheme = useStore((state) => state.setTheme);
  const toggleSidebar = useStore((state) => state.toggleSidebar);
  const toggleMobileMenu = useStore((state) => state.toggleMobileMenu);

  return {
    theme,
    sidebarState,
    isMobileMenuOpen,
    setTheme,
    toggleSidebar,
    toggleMobileMenu,
  };
};

export const useTheme = () => useStore((state) => state.theme);
export const useSidebarState = () => useStore((state) => state.sidebarState);
export const useNotifications = () => useStore((state) => state.notifications);
export const useModals = () => useStore((state) => state.modals);

/**
 * Notification helpers
 */
export const useNotify = () => {
  const addNotification = useStore((state) => state.addNotification);

  return {
    success: (title: string, message?: string) =>
      addNotification({ type: 'success', title, message, duration: 3000 }),
    error: (title: string, message?: string) =>
      addNotification({ type: 'error', title, message, duration: 5000 }),
    warning: (title: string, message?: string) =>
      addNotification({ type: 'warning', title, message, duration: 4000 }),
    info: (title: string, message?: string) =>
      addNotification({ type: 'info', title, message, duration: 3000 }),
  };
};

/**
 * Modal helpers
 */
export const useModal = () => {
  const openModal = useStore((state) => state.openModal);
  const closeModal = useStore((state) => state.closeModal);
  const closeAllModals = useStore((state) => state.closeAllModals);

  return {
    open: openModal,
    close: closeModal,
    closeAll: closeAllModals,
  };
};

/**
 * Global loading state
 */
export const useGlobalLoading = () => {
  const isLoading = useStore((state) => state.globalLoading);
  const message = useStore((state) => state.loadingMessage);
  const setLoading = useStore((state) => state.setGlobalLoading);

  return { isLoading, message, setLoading };
};

// ============= Store Subscriptions =============

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuth = (callback: (isAuthenticated: boolean) => void) => {
  return useStore.subscribe((state) => state.isAuthenticated, callback);
};

/**
 * Subscribe to workspace changes
 */
export const subscribeToWorkspace = (callback: (workspace: any) => void) => {
  return useStore.subscribe((state) => state.currentWorkspace, callback);
};

/**
 * Subscribe to theme changes
 */
export const subscribeToTheme = (callback: (theme: any) => void) => {
  return useStore.subscribe((state) => state.theme, callback);
};

// ============= Store Actions =============

/**
 * Initialize the store on app startup
 */
export const initializeStore = async () => {
  const state = useStore.getState();

  // If authenticated, fetch workspaces
  if (state.isAuthenticated && state.user) {
    try {
      await state.fetchWorkspaces();
    } catch (error) {
      console.error('Failed to initialize workspaces:', error);
    }
  }
};

// Export the store for direct access if needed
export default useStore;
